// src/EventsSubsection.jsx

import React, { useState, useEffect, useRef } from 'react';

export default function EventsSubsection({
  initialData = [],
  onDataChange,
  isEditMode = false,
}) {
  const [events, setEvents] = useState(initialData);
  const skipNextNotify = useRef(false);

  // 1️⃣ Sync props→state only when data truly changes
  useEffect(() => {
    const prev = JSON.stringify(events);
    const next = JSON.stringify(initialData);
    if (prev !== next) {
      setEvents(initialData);
      skipNextNotify.current = true;
    }
  }, [initialData]);

  // 2️⃣ Notify parent when events change (but skip the first sync)
  useEffect(() => {
    if (skipNextNotify.current) {
      skipNextNotify.current = false;
      return;
    }
    if (JSON.stringify(events) !== JSON.stringify(initialData)) {
      onDataChange(events);
    }
  }, [events, initialData, onDataChange]);

  const add = () =>
    setEvents(prev => [
      ...prev,
      {
        eventDate: '',
        typeOfPaint: '',
        drydocking: false,
        hullCleaning: false,
        propellerPolishing: false,
        meOverhaul: false,
        uwInspection: false,
        retrofits: '',
        shaftMeterCalibration: false,
        speedLogCalibration: false,
        comments: '',
      },
    ]);

  const remove = idx =>
    setEvents(prev => prev.filter((_, i) => i !== idx));

  const handle = (idx, field, value) =>
    setEvents(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });

  return (
    <div className="mb-4">
      <h5 className="fw-bold">Events</h5>

      {events.map((ev, idx) => (
        <div key={idx} className="border p-3 mb-3">
          <h6>Event {idx + 1}</h6>
          <div className="row g-2">
            {/* Date & Paint Type */}
            <div className="col-md-3">
              <label className="form-label">Event Date</label>
              <input
                type="date"
                className="form-control"
                value={ev.eventDate?.slice(0, 10) || ''}
                onChange={e => handle(idx, 'eventDate', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Type of Paint</label>
              <input
                type="text"
                className="form-control"
                value={ev.typeOfPaint || ''}
                onChange={e => handle(idx, 'typeOfPaint', e.target.value)}
              />
            </div>

            {/* Checkboxes */}
            {[
              'drydocking',
              'hullCleaning',
              'propellerPolishing',
              'meOverhaul',
              'uwInspection',
              'shaftMeterCalibration',
              'speedLogCalibration',
            ].map(fld => (
              <div className="col-md-2" key={fld}>
                <div className="form-check mt-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={!!ev[fld]}
                    onChange={e => handle(idx, fld, e.target.checked)}
                  />
                  <label className="form-check-label">
                    {fld
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, c => c.toUpperCase())}
                  </label>
                </div>
              </div>
            ))}

            {/* Retrofits & Comments */}
            <div className="col-md-6">
              <label className="form-label">Retrofits</label>
              <input
                type="text"
                className="form-control"
                value={ev.retrofits || ''}
                onChange={e => handle(idx, 'retrofits', e.target.value)}
              />
            </div>
            <div className="col-md-12 mt-2">
              <label className="form-label">Comments</label>
              <textarea
                className="form-control"
                value={ev.comments || ''}
                onChange={e => handle(idx, 'comments', e.target.value)}
              />
            </div>

            {!isEditMode && (
              <div className="col-md-1 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => remove(idx)}
                >
                  &times;
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {!isEditMode && (
        <button type="button" className="btn btn-secondary" onClick={add}>
          + Add Event
        </button>
      )}
    </div>
  );
}
